import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { X, Search, User } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { gymModalHeader, gymModalInput, gymModalPanel } from './gymPageTheme';

export default function ClientSelectionModal({ isOpen, onClose, onSelect }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen) {
            fetchClients();
        }
    }, [isOpen]);

    const fetchClients = async () => {
        try {
            setLoading(true);
            setError(null);
            const api = getAuthenticatedApi();

            const response = await api.get('/trainer_clients.php');

            if (response.data.success) {
                setClients(response.data.clients || []);
            } else {
                setClients([]);
            }
        } catch (err) {
            console.error('Errore nel recupero clienti:', err);
            setError(t('gym.clientSelectionModal.errorLoadingClients'));
            setClients([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredClients = clients.filter(client =>
        client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (client) => {
        onSelect({
            client_id: client.id,
            client_name: client.name,
            client_email: client.email
        });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={`${gymModalPanel} flex max-h-[80vh] max-w-2xl flex-col`}>
                <div className={gymModalHeader}>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {t('gym.clientSelectionModal.title')}
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-800 dark:text-white/70 dark:hover:text-white"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="border-b border-gray-200 p-4 dark:border-white/10">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-gray-400 dark:text-white/45" />
                        <input
                            type="text"
                            placeholder={t('gym.clientSelectionModal.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={`${gymModalInput} pl-10`}
                        />
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner />
                        </div>
                    ) : error ? (
                        <div className="py-8 text-center">
                            <p className="text-red-600 dark:text-red-300">{error}</p>
                            <button
                                type="button"
                                onClick={fetchClients}
                                className="mt-2 rounded-md bg-indigo-600 px-4 py-2 text-white hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                            >
                                {t('gym.clientSelectionModal.retry')}
                            </button>
                        </div>
                    ) : filteredClients.length === 0 ? (
                        <div className="py-8 text-center">
                            <User className="mx-auto mb-4 h-12 w-12 text-gray-400 dark:text-white/40" />
                            <p className="text-gray-600 dark:text-white/65">
                                {searchTerm ? t('gym.clientSelectionModal.noClientsFound') : t('gym.clientSelectionModal.noClientsAvailable')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {filteredClients.map((client) => (
                                <div
                                    key={client.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleSelect(client)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSelect(client)}
                                    className="cursor-pointer rounded-lg border border-gray-200 bg-gray-50 p-3 transition-colors hover:bg-gray-100 dark:border-white/10 dark:bg-gray-900 dark:hover:bg-gray-700"
                                >
                                    <div className="flex items-center space-x-3">
                                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 dark:bg-gray-900">
                                            <User className="h-5 w-5 text-white dark:text-gray-300" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-medium text-gray-900 dark:text-white">{client.name}</p>
                                            {client.email && (
                                                <p className="truncate text-sm text-gray-500 dark:text-white/60">{client.email}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
